// Chetan clips grid (Front Row style).
// Shows up to N clip cards for a company with thumbnail + title, then a
// single "Browse all minis on Shubh Labh — Favcy Media" CTA at the bottom.
// Cards open the public ShubhLabh page in a new tab. No iframe. No Vimeo
// embed. Views and branding stay on Favcy property.
//
// Usage:
//   <ChetanClipsGrid endpoint="/api/founder-portal/clips" />
//   <ChetanClipsGrid endpoint={`/api/admin/founder-portal/companies/${id}/clips`} authHeader={{ "x-admin-token": token }} />
//   <ChetanClipsGrid endpoint={`/api/public/companies/${id}/clips`} tone="dark" />
import { useEffect, useState, type MouseEvent as ReactMouseEvent } from "react";
import { ArrowUpRight, Play, Video, X } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

// Defence in depth: clip URLs come from a third-party (ShubhLabh / Chetan).
// Block any scheme other than http/https before we hand them to <a href> or
// <img src> so a poisoned title/thumbnail can't smuggle javascript:/data:
// payloads. Returns null when the URL is not safe.
function safeHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.toString();
  } catch {
    return null;
  }
}

interface Clip {
  slug: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  publicUrl: string | null;
  videoUrl: string | null;
  publishedAt: string | null;
}

// Pull the numeric Vimeo id out of either a vimeo.com/{id} or a
// player.vimeo.com/video/{id} URL. Anything else returns null and we fall
// back to opening the public ShubhLabh page in a new tab.
function vimeoEmbedUrl(raw: string | null | undefined): string | null {
  const safe = safeHttpUrl(raw);
  if (!safe) return null;
  try {
    const u = new URL(safe);
    if (!/(^|\.)vimeo\.com$/.test(u.hostname)) return null;
    const m = u.pathname.match(/(?:\/video)?\/(\d+)(?:\/|$)/);
    if (!m) return null;
    return `https://player.vimeo.com/video/${m[1]}?autoplay=1&dnt=1&title=0&byline=0&portrait=0`;
  } catch {
    return null;
  }
}

interface ApiResponse {
  clips: Clip[];
  total: number;
  browseAllUrl: string;
  companyName: string;
}

export interface ChetanClipsGridProps {
  /** Path under BASE_URL, e.g. "api/founder-portal/clips" or "api/public/companies/5/clips". */
  endpoint: string;
  /** Optional auth headers (admin token, etc). Founder token is read automatically when not supplied. */
  authHeader?: Record<string, string>;
  /** How many cards to show. Default 12 like Front Row's row of 4 x 3. */
  limit?: number;
  /** "dark" matches the founder OS / investor login. "light" for the investor portal panels. */
  tone?: "dark" | "light";
  /** Eyebrow heading. Default "Your clips on Shubh Labh". Hidden when null. */
  heading?: string | null;
  /** Sub-heading. */
  subheading?: string | null;
  /** Hide the entire block when there are no clips at all (default true). */
  hideWhenEmpty?: boolean;
}

export function ChetanClipsGrid({
  endpoint,
  authHeader,
  limit = 12,
  tone = "dark",
  heading = "Featured on Shubh Labh",
  subheading = "Founder clips from Kal Ke Krorepati and Favcy Lounge episodes.",
  hideWhenEmpty = true,
}: ChetanClipsGridProps) {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeClip, setActiveClip] = useState<Clip | null>(null);

  // Lock body scroll while the lightbox is open and let Esc close it.
  useEffect(() => {
    if (!activeClip) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setActiveClip(null); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [activeClip]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const headers: Record<string, string> = { ...(authHeader || {}) };
        if (!authHeader && !endpoint.includes("/public/") && !endpoint.includes("/admin/")) {
          const tok = localStorage.getItem("founder_token");
          if (tok) headers["x-founder-token"] = tok;
        }
        const url = `${BASE}${endpoint.replace(/^\//, "")}${endpoint.includes("?") ? "&" : "?"}limit=${limit}`;
        const r = await fetch(url, { headers });
        if (!r.ok) {
          if (!cancelled) setData(null);
          return;
        }
        const j = (await r.json()) as ApiResponse;
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, limit, JSON.stringify(authHeader || {})]);

  const isDark = tone === "dark";
  const wrapperBg = isDark ? "bg-[#0A0F2C] border border-white/[0.08]" : "bg-slate-950 border border-slate-800";
  const headingColor = isDark ? "text-white" : "text-white";
  const subColor = isDark ? "text-white/60" : "text-white/60";
  const cardBg = isDark ? "bg-white/[0.04] border border-white/[0.08] hover:border-white/20" : "bg-slate-900 border border-slate-800 hover:border-slate-600";
  const titleColor = isDark ? "text-white/90" : "text-white/90";
  const ctaBorder = isDark ? "border-white/15 hover:border-[#FF5C00]/60" : "border-slate-700 hover:border-[#FF5C00]/60";
  const ctaText = isDark ? "text-white/80 hover:text-white" : "text-white/80 hover:text-white";

  if (loading) {
    return (
      <div className={`rounded-2xl ${wrapperBg} p-6 md:p-8`}>
        <div className="h-4 w-48 bg-white/10 rounded animate-pulse mb-2" />
        <div className="h-3 w-64 bg-white/5 rounded animate-pulse mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-video rounded-lg bg-white/[0.04] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;
  if (hideWhenEmpty && data.clips.length === 0) return null;

  return (
    <div className={`rounded-2xl ${wrapperBg} p-6 md:p-8`} data-testid="chetan-clips-grid">
      {(heading || subheading) && (
        <div className="mb-6">
          {heading && (
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-[#FF5C00]" />
              <h3 className={`text-base md:text-lg font-serif font-bold ${headingColor}`}>{heading}</h3>
            </div>
          )}
          {subheading && <p className={`text-xs md:text-sm ${subColor}`}>{subheading}</p>}
        </div>
      )}

      {data.clips.length === 0 ? (
        <div className="text-center py-10">
          <p className={`text-sm ${subColor}`}>No clips found yet for {data.companyName} on Shubh Labh.</p>
          <a
            href={safeHttpUrl(data.browseAllUrl) || "#"}
            target="_blank"
            rel="noreferrer"
            className={`mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider ${ctaText} hover:text-[#FF5C00]`}
          >
            Browse Shubh Labh <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {data.clips.map(clip => {
              const safeThumb = safeHttpUrl(clip.thumbnailUrl);
              const embed = vimeoEmbedUrl(clip.videoUrl);
              const fallbackHref = safeHttpUrl(clip.publicUrl) || safeHttpUrl(data.browseAllUrl) || "#";
              const onClick = (e: ReactMouseEvent) => {
                if (embed) { e.preventDefault(); setActiveClip(clip); }
              };
              return (
                <a
                  key={clip.slug}
                  href={fallbackHref}
                  onClick={onClick}
                  target={embed ? undefined : "_blank"}
                  rel="noreferrer"
                  data-testid={`chetan-clip-${clip.slug}`}
                  className={`group block rounded-lg overflow-hidden ${cardBg} transition-all relative cursor-pointer text-left`}
                >
                  <div className="aspect-video bg-black/40 relative overflow-hidden">
                    {safeThumb ? (
                      <img
                        src={safeThumb}
                        alt={clip.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-8 h-8 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    <div className="absolute top-2 left-2">
                      <span className="w-2 h-2 rounded-full bg-[#FF5C00] inline-block shadow-[0_0_10px_#FF5C00]" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-[#FF5C00]/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className={`text-xs md:text-[13px] font-serif font-medium ${titleColor} leading-snug line-clamp-2`}>
                        {clip.title}
                      </p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="mt-6 md:mt-8">
            <a
              href={safeHttpUrl(data.browseAllUrl) || "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="chetan-browse-all"
              className={`block w-full text-center border ${ctaBorder} ${ctaText} rounded-md py-3 text-[11px] md:text-xs font-mono uppercase tracking-[0.2em] transition-colors group`}
            >
              <span className="inline-flex items-center gap-2">
                Browse all {data.total > data.clips.length ? `${data.total} ` : ""}minis on Shubh Labh
                <span className="opacity-50">·</span>
                <span className="text-[#FF5C00]">Favcy Media</span>
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </a>
          </div>
        </>
      )}

      {activeClip && (() => {
        const embed = vimeoEmbedUrl(activeClip.videoUrl);
        const fallback = safeHttpUrl(activeClip.publicUrl);
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={activeClip.title}
            data-testid="chetan-clip-modal"
            onClick={() => setActiveClip(null)}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200"
          >
            <div
              onClick={e => e.stopPropagation()}
              className="relative w-full max-w-4xl rounded-2xl overflow-hidden bg-[#0A0F2C] border border-white/10 shadow-2xl"
            >
              <button
                type="button"
                onClick={() => setActiveClip(null)}
                aria-label="Close"
                data-testid="chetan-clip-modal-close"
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white/90 hover:text-white flex items-center justify-center transition-colors border border-white/10"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="aspect-video bg-black">
                {embed ? (
                  <iframe
                    src={embed}
                    title={activeClip.title}
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="w-full h-full border-0"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
                    Video unavailable.
                    {fallback && (
                      <a href={fallback} target="_blank" rel="noreferrer" className="ml-2 text-[#FF5C00] underline">
                        Open on Shubh Labh
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="px-5 md:px-6 py-4 md:py-5">
                <h4 className="font-serif text-base md:text-lg text-white leading-snug">
                  {activeClip.title}
                </h4>
                {activeClip.description && (
                  <p className="mt-1.5 text-xs md:text-sm text-white/70 leading-relaxed line-clamp-3">
                    {activeClip.description}
                  </p>
                )}
                {fallback && (
                  <a
                    href={fallback}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-[11px] md:text-xs font-mono uppercase tracking-[0.18em] text-white/60 hover:text-[#FF5C00] transition-colors"
                  >
                    Open on Shubh Labh <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
